import Phaser from 'phaser';
import { MENU_ITEMS } from '../../data/menu';
import {
  createGorkemSideTable,
  createGorkemVisual,
  setCharacterAnimation,
  setCharacterFacing,
  setGorkemBubble,
  setGorkemSeated,
  setGorkemWaving,
} from '../cafe/VisualFactory';
import type { SpecialEventDefinition } from '../types';
import type { AudioSystem } from './AudioSystem';
import { GorkemStateMachine } from './GorkemStateMachine';

export const GORKEM_CUSTOMER_ID = -1000;

export class GorkemEventSystem {
  readonly stateMachine = new GorkemStateMachine();
  readonly visual: Phaser.GameObjects.Container;
  private definition?: SpecialEventDefinition;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly audio: AudioSystem,
    private readonly onInteraction: () => void,
    private readonly announce: (message: string, tone?: 'good' | 'info' | 'warning') => void,
  ) {
    createGorkemSideTable(scene);
    this.visual = createGorkemVisual(scene, 48, 176).setVisible(false);
    this.visual.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, event: Phaser.Types.Input.EventData) => {
      event.stopPropagation();
      this.onInteraction();
    });
  }

  get state() {
    return this.stateMachine.state;
  }

  get event(): SpecialEventDefinition | undefined {
    return this.definition;
  }

  start(definition: SpecialEventDefinition): boolean {
    if (this.state !== 'INACTIVE') return false;
    this.definition = definition;
    this.stateMachine.transition('ENTERING');
    this.audio.play('gorkem');
    this.announce('Sürpriz! Görkem köşeye uğradı.', 'good');
    this.visual.setVisible(true).setAlpha(0).setScale(0.72).setPosition(48, 176);
    setCharacterFacing(this.visual, 1);
    setCharacterAnimation(this.visual, 'enterWalk', 'right');
    this.scene.tweens.add({
      targets: this.visual, x: 150, y: 330, alpha: 1, scale: 1, duration: 850, ease: 'Back.out',
      onComplete: () => this.walkToTable(),
    });
    return true;
  }

  orderTaken(): boolean {
    if (this.state !== 'WAITING_FOR_ORDER' || !this.definition) return false;
    this.stateMachine.transition('WAITING_FOR_FOOD');
    setCharacterAnimation(this.visual, 'order', 'down');
    setGorkemBubble(this.visual, '◷');
    this.audio.play('order');
    this.announce(`${MENU_ITEMS[this.definition.order].name} Görkem için hazırlanıyor.`, 'info');
    this.scene.time.delayedCall(520, () => {
      if (this.state === 'WAITING_FOR_FOOD') setCharacterAnimation(this.visual, 'waiting', 'down');
    });
    return true;
  }

  served(): number {
    if (this.state !== 'WAITING_FOR_FOOD' || !this.definition) return 0;
    this.stateMachine.transition('GIFTING');
    setCharacterAnimation(this.visual, 'gift', 'down');
    setGorkemBubble(this.visual, '🎁');
    this.audio.play('gorkem');
    this.announce(`Görkem’den küçük bir hediye: +₺${this.definition.bonus}`, 'good');
    const bonus = this.definition.bonus;
    this.scene.time.delayedCall(850, () => this.waveAndLeave());
    return bonus;
  }

  private walkToTable(): void {
    this.stateMachine.transition('WALKING_TO_TABLE');
    setCharacterFacing(this.visual, 1);
    setCharacterAnimation(this.visual, 'walk', 'right');
    this.scene.tweens.add({
      targets: this.visual, x: 1120, y: 448, duration: 1800, ease: 'Sine.inOut',
      onComplete: () => {
        this.stateMachine.transition('SITTING');
        setCharacterAnimation(this.visual, 'sitDown', 'down');
        this.visual.setDepth(26);
        this.scene.time.delayedCall(420, () => {
          if (!this.definition) return;
          setGorkemSeated(this.visual, true);
          this.stateMachine.transition('WAITING_FOR_ORDER');
          setGorkemBubble(this.visual, '', this.definition.order);
          this.announce('Görkem özel köşesinde sipariş vermeye hazır.', 'info');
        });
      },
    });
  }

  private waveAndLeave(): void {
    this.stateMachine.transition('WAVING');
    setGorkemBubble(this.visual, '♥');
    setGorkemWaving(this.visual, true);
    this.scene.time.delayedCall(1000, () => {
      this.stateMachine.transition('LEAVING');
      setGorkemSeated(this.visual, false);
      setGorkemWaving(this.visual, false);
      setGorkemBubble(this.visual, '');
      setCharacterFacing(this.visual, -1);
      setCharacterAnimation(this.visual, 'leaveWalk', 'left');
      this.visual.setDepth(31);
      this.scene.tweens.add({
        targets: this.visual, x: 55, y: 182, alpha: 0.72, scale: 0.74, duration: 1700, ease: 'Sine.inOut',
        onComplete: () => {
          this.stateMachine.transition('COMPLETE');
          this.visual.setVisible(false);
        },
      });
    });
  }
}
